import React from 'react';

/**
 * Is the visitor engaging with the site navigation right now?
 *
 * The landing page answers this in two places at once — the header thickens its
 * fill and the reel behind it dims — so the state lives here rather than in
 * either of them. Detection reads the DOM instead of asking the header to
 * report up: the header keeps no knowledge of what is behind it.
 */
const Context = React.createContext(false);

// The header, and the mobile menu panel that lives outside it.
const NAV_SELECTOR = '[data-site-header], #mobile-menu';
// Menus already publish their own open state for screen readers.
const OPEN_MENU_SELECTOR = '[data-site-header] [aria-expanded="true"]';

const isInNav = (node: EventTarget | null) =>
  node instanceof Element && Boolean(node.closest(NAV_SELECTOR));

const isMenuOpen = () => Boolean(document.querySelector(OPEN_MENU_SELECTOR));

/**
 * Clicking a control leaves focus sitting on it long after the pointer has
 * gone. Counting that as engagement strands the page in its engaged state
 * until something else is clicked, so only keyboard focus counts here — which
 * is the case that actually needs it, since a keyboard user has no pointer to
 * signal with.
 */
const isKeyboardFocusInNav = () => {
  const active = document.activeElement;
  if (!isInNav(active)) return false;

  try {
    return (active as Element).matches(':focus-visible');
  } catch {
    // No :focus-visible support: count any focus. Keeping the keyboard
    // affordance is worth more than avoiding a stuck dim on an old browser.
    return true;
  }
};

interface INavEngagementProviderProps {
  /** Only the landing page reacts to this, so listeners run only there. */
  active: boolean;
  children: React.ReactNode;
}

export const NavEngagementProvider = ({
  active,
  children
}: INavEngagementProviderProps) => {
  const [isEngaged, setIsEngaged] = React.useState(false);

  // Only the pointer needs remembering. Focus is read live from the document so
  // a stale "focus is in the nav" can't strand the page in its engaged state.
  const pointerInNav = React.useRef(false);

  const sync = React.useCallback(() => {
    setIsEngaged(pointerInNav.current || isKeyboardFocusInNav() || isMenuOpen());
  }, []);

  React.useEffect(() => {
    if (!active) {
      pointerInNav.current = false;
      setIsEngaged(false);
      return undefined;
    }

    let frame = 0;
    // A menu closes on the very click or keypress being handled here, so its
    // state has to be re-read after React commits, not during the event.
    const syncAfterCommit = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    };

    // pointerover fires on entering any element, so one handler covers both
    // arriving in the header and leaving it for the page behind.
    const onPointerOver = (e: PointerEvent) => {
      pointerInNav.current = isInNav(e.target);
      sync();
    };
    const onPointerLeave = () => {
      pointerInNav.current = false;
      sync();
    };
    // focusout lands before activeElement settles, so re-read after commit.
    const onFocusIn = () => sync();
    const onFocusOut = () => syncAfterCommit();

    document.addEventListener('pointerover', onPointerOver);
    document.addEventListener('pointerleave', onPointerLeave);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('click', syncAfterCommit);
    document.addEventListener('keyup', syncAfterCommit);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('pointerover', onPointerOver);
      document.removeEventListener('pointerleave', onPointerLeave);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('click', syncAfterCommit);
      document.removeEventListener('keyup', syncAfterCommit);
    };
  }, [active, sync]);

  return <Context.Provider value={active && isEngaged}>{children}</Context.Provider>;
};

const useNavEngagement = () => React.useContext(Context);

export default useNavEngagement;
